import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'store/store'
import {
  Accordion,
  Alert,
  Breadcrumb,
  Card,
  Icon,
  Input,
  LoadingModal,
  Modal,
  Page,
  Select,
  SlimButton,
  Typography,
  UploadArea
} from 'components'
import { Controller, useForm } from 'react-hook-form'
import { FranchiseFormValues } from 'services/batchOrder/interfaces'
import {
  clearGeneratedData,
  clearUploadData,
  deleteFranchiseUpload,
  SendFranchiseOrders,
  sendFranchiseUpload
} from 'store/BatchOrderContext/BatchOrderSlice'
import {
  Channels,
  Company,
  Offices,
  Organization,
  companies,
  sectorOptions
} from 'constants/enums'
import { yupResolver } from '@hookform/resolvers/yup'
import Footer from '../components/Footer'

import * as S from './styles'
import { formSchema } from './validations'
import {
  getCommercialStructure,
  getQuotasByOffice
} from 'store/SharedContext/sharedSlice'
import { ROUTES } from 'constants/urls'
import { useNavigate } from 'react-router-dom'
import UploadSpreadsheetTableError from '../components/UploadSpreadsheetTableError'
import ModalConfirmGeneration from './components/ModalConfirmGeneration'
import { useCommercialStructure } from 'commons/customHooks'
import { theme } from 'styles/theme/hav-theme'

const Franchises = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    loadingUpload,
    loadingSend,
    loadingDeletion,
    hasConfirmedFranchiseOrders,
    uploadData: {
      processId,
      franchiseErrors,
      uploadSuccess,
      hasOrders,
      errorDownloadLink
    }
  } = useSelector((state) => state.batchOrder)

  const { quotas, loadingCommercialStructure, loadingQuotas } = useSelector(
    (state) => state.shared
  )
  const officesCommercialStructure = useSelector((state) => state.shared.offices)

  const { getMarkets, getOfficesByMarkets, getRegionalsByOffices, getZonesByRegionals } =
    useCommercialStructure()

  const [uploadProgress, setUploadProgress] = useState(0)
  const [modalDeleteOrders, setModalDeleteOrders] = useState(false)
  const [modalConfirmGeneration, setModalConfirmGeneration] = useState(false)

  const hasUploadError = !!processId && !!franchiseErrors.length
  const disableForm =
    loadingUpload ||
    hasOrders ||
    loadingCommercialStructure ||
    loadingQuotas ||
    hasUploadError

  const company = [
    {
      title: companies[Company.ALPA].title,
      value: companies[Company.ALPA].value
    }
  ]

  const defaultValues: FranchiseFormValues = {
    company: Company.ALPA,
    organization: Organization.ALHV,
    market: '',
    sector: '',
    office: '',
    region: '',
    zone: '',
    quotaMonth: '',
    file: null,
    initialDataLine: '8',
    initialSizeCol: '23',
    finalSizeCol: '50',
    initialClientCol: '52',
    finalClientCol: '171'
  }

  const {
    control,
    getValues,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid }
  } = useForm<FranchiseFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema)
  })

  const markets = getMarkets()
  const selectedMarket = watch('market') as Channels
  const selectedSector = watch('sector')
  const selectedOffice = watch('office') as Offices
  const selectedRegion = watch('region')
  const fileName = watch('file.name')

  const onSubmit = handleSubmit((data) => {
    dispatch(sendFranchiseUpload(data, setUploadProgress))
  })

  const handleOrderDeletion = () => {
    setModalDeleteOrders(false)
    dispatch(deleteFranchiseUpload(fileName))
  }

  const handleOrderSend = () => {
    dispatch(SendFranchiseOrders(fileName))
  }

  const handleErrorDownload = () => {
    if (errorDownloadLink) {
      window.open(errorDownloadLink, '_blank')
    }
  }

  useEffect(() => {
    if (!officesCommercialStructure.length) {
      dispatch(getCommercialStructure())
    }
  }, [officesCommercialStructure])

  useEffect(() => {
    dispatch(clearUploadData())
    dispatch(clearGeneratedData())
  }, [])

  useEffect(() => {
    if (selectedOffice) {
      dispatch(getQuotasByOffice(selectedOffice))
    }
  }, [selectedOffice])

  useEffect(() => {
    if (hasConfirmedFranchiseOrders) {
      setModalConfirmGeneration(true)
    }
  }, [hasConfirmedFranchiseOrders])

  const officeOptions = useMemo(() => {
    if (!selectedMarket) return []

    return getOfficesByMarkets([selectedMarket])
  }, [selectedMarket])

  const regionOptions = useMemo(() => {
    return getRegionalsByOffices([selectedOffice])
  }, [selectedOffice])

  const zoneOptions = useMemo(() => {
    return getZonesByRegionals([selectedRegion])
  }, [selectedRegion])

  const validateNumber = (value: string) => {
    const regex = /^\d+$/

    return regex.test(value) || value === ''
  }

  const InsertionHeader = () => (
    <S.ColWrapper>
      <Typography design="hav-h6-text">Inserção de material</Typography>
      <Typography design="hav-h7-text">
        Após preencher os campos acima, carregue a planilha e solicite os materiais.
      </Typography>
    </S.ColWrapper>
  )

  const SpreadsheetInfo = () => (
    <S.InfoWrapper>
      <Icon icon="info" color={theme.colors.primary.main} size="16" />
      <Typography design="hav-default-03-text">
        Certifique-se de que a planilha carregada tenha o pedido na primeira aba, pois
        apenas ela será processada.
      </Typography>
    </S.InfoWrapper>
  )

  return (
    <Page fullwidth>
      <Breadcrumb />
      <S.Wrapper>
        <Accordion summaryContent={<S.Title>Pedido franquias massivo</S.Title>}>
          <S.Form onSubmit={onSubmit}>
            <S.Field>
              <Controller
                name="company"
                control={control}
                defaultValue={Company.ALPA}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Empresa"
                    disabled
                    value={value}
                    id="company"
                    name={name}
                    options={company}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="organization"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Organização de venda"
                    disabled
                    value={value}
                    id="organization"
                    name={name}
                    options={[{ title: 'ALHV - Havaianas', value: Organization.ALHV }]}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="market"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Mercado"
                    value={value}
                    id="market"
                    name={name}
                    disabled={disableForm}
                    options={markets.map((market) => ({
                      title: market.id,
                      value: market.id
                    }))}
                    menuText="Clique para selecionar"
                    onChange={(e) => {
                      setValue('office', '')
                      setValue('region', '')
                      setValue('zone', '')
                      setValue('quotaMonth', '')
                      onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="sector"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Setor"
                    value={value}
                    id="sector"
                    disabled={!selectedMarket || disableForm}
                    menuText={
                      !selectedMarket ? 'Selecione um mercado' : 'Clique para selecionar'
                    }
                    name={name}
                    options={sectorOptions}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="office"
                control={control}
                render={({ field: { name, value, onChange }, fieldState }) => (
                  <Select
                    label="Escritório de venda"
                    value={value}
                    id="office"
                    name={name}
                    disabled={!officeOptions.length || !selectedSector || disableForm}
                    menuText={
                      !selectedSector ? 'Selecione um setor' : 'Clique para selecionar'
                    }
                    options={officeOptions.map((office) => ({
                      title: `${office.id} - ${office.description}`,
                      value: office.id
                    }))}
                    onChange={(e) => {
                      setValue('region', '')
                      setValue('zone', '')
                      onChange(e)
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="region"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Regional"
                    value={value}
                    id="region"
                    name={name}
                    disabled={!regionOptions.length || !selectedOffice || disableForm}
                    menuText={
                      !selectedOffice
                        ? 'Selecione um escritório'
                        : 'Clique para selecionar'
                    }
                    options={regionOptions.map((region) => ({
                      title: `${region.id} - ${region.description}`,
                      value: region.id
                    }))}
                    onChange={(e) => {
                      setValue('zone', '')
                      onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="zone"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Zona"
                    value={value}
                    id="zone"
                    name={name}
                    disabled={!zoneOptions.length || !selectedRegion || disableForm}
                    menuText={
                      !selectedRegion
                        ? 'Selecione uma regional'
                        : 'Clique para selecionar'
                    }
                    options={zoneOptions.map((zone) => ({
                      title: `${zone.id} - ${zone.description}`,
                      value: zone.id
                    }))}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </S.Field>
            <S.Field>
              <Controller
                name="quotaMonth"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Mês cota"
                    value={value}
                    id="quotaMonth"
                    name={name}
                    disabled={!quotas.length || !selectedOffice || disableForm}
                    menuText={
                      !selectedOffice
                        ? 'Selecione um escritório'
                        : 'Clique para selecionar'
                    }
                    options={quotas.map((quota) => ({
                      title: quota.MesCota,
                      value: quota.Descricao
                    }))}
                    onChange={(e) => onChange(e)}
                  />
                )}
              />
            </S.Field>
            <S.HozDivider />
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Dados: Linha inicial</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('initialDataLine') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Tamanho: Coluna inicial</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('initialSizeCol') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
            <S.Field span={1}>
              <Controller
                name="finalSizeCol"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label="Tamanho: Coluna final"
                    error={fieldState.error?.message}
                    maxLength={4}
                    disabled={disableForm}
                    onChange={(e) => {
                      if (validateNumber(e.target.value)) field.onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field span={1}>
              <Controller
                name="initialClientCol"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label="Cliente: Coluna inicial"
                    error={fieldState.error?.message}
                    maxLength={4}
                    disabled={disableForm}
                    onChange={(e) => {
                      if (validateNumber(e.target.value)) field.onChange(e)
                    }}
                  />
                )}
              />
            </S.Field>
            <S.Field span={1}>
              <S.SizeWrapper>
                <Typography design="hav-h8-text">Cliente: Coluna final</Typography>
                <Typography design="hav-default-03-text">
                  {getValues('finalClientCol') ?? '-'}
                </Typography>
              </S.SizeWrapper>
            </S.Field>
          </S.Form>
        </Accordion>
        {hasUploadError && !hasOrders && (
          <Alert
            kind="error"
            title="Erros na planilha!"
            message="Foram identificados alguns erros na planilha. Verifique os detalhes abaixo e faça as correções necessárias antes de reenviá-la."
            fullWidth
          />
        )}
        {hasUploadError && hasOrders && (
          <Alert
            kind="warning"
            title="Foram identificados alguns erros na planilha!"
            message='Confira abaixo e clique em "Confirmar" caso deseje continuar mesmo assim. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            fullWidth
          />
        )}
        {!hasUploadError && hasOrders && (
          <Alert
            kind="info"
            title="Pedido em aberto!"
            message='Clique em "solicitar" para gerar as OVs. Caso contrário, os pedidos ficarão em aberto na área de gerenciamento.'
            fullWidth
          />
        )}
        {!processId && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <SpreadsheetInfo />
              {isValid && (
                <>
                  <S.Divider />
                  <Controller
                    name="file"
                    control={control}
                    defaultValue={{} as File}
                    render={({ field: { name, onChange } }) => (
                      <UploadArea
                        id="file"
                        name={name}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        loading={loadingUpload}
                        loadingProgress={uploadProgress}
                        uploadAreaText={
                          <>
                            <strong>Clique aqui</strong> ou arraste o arquivo da sua
                            planilha para enviá-la.
                          </>
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files) {
                            onChange(e.target.files[0])
                            onSubmit()
                            e.target.value = ''
                          }
                        }}
                      />
                    )}
                  />
                </>
              )}
            </S.InsertionWrapper>
          </Card>
        )}
        {hasUploadError && (
          <Card>
            <S.ErrorWrapper>
              <S.ErrorOrderHeader>
                <S.ErrorOrderActions>
                  {!hasOrders && (
                    <SlimButton
                      type="button"
                      variant="primary"
                      content="Voltar"
                      icon={<Icon icon="undo" size="15" />}
                      onClick={() => dispatch(clearUploadData())}
                    />
                  )}
                  <SlimButton
                    type="button"
                    variant="primary"
                    content="Baixar"
                    icon={<Icon icon="download" size="15" />}
                    onClick={handleErrorDownload}
                  />
                </S.ErrorOrderActions>
                {hasOrders && (
                  <Footer
                    amountOfPairs={uploadSuccess.pairVol}
                    amountOfBoxes={uploadSuccess.boxVol}
                    totalValue={uploadSuccess.totalVal}
                    loading={loadingSend || loadingDeletion}
                    onDelete={() => setModalDeleteOrders(true)}
                    onSubmit={handleOrderSend}
                  />
                )}
              </S.ErrorOrderHeader>
              <S.UploadedFileViewer>
                <Icon icon="paper-clip" color={theme.colors.primary.main} size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <UploadSpreadsheetTableError data={franchiseErrors} />
            </S.ErrorWrapper>
          </Card>
        )}
        {hasOrders && !hasUploadError && (
          <Card>
            <S.InsertionWrapper>
              <InsertionHeader />
              <SpreadsheetInfo />
              <S.Divider />
              <S.UploadedFileViewer>
                <Icon icon="paper-clip" color={theme.colors.primary.main} size="15" />
                <Typography design="hav-h7-text">{fileName}</Typography>
              </S.UploadedFileViewer>
              <Footer
                amountOfPairs={uploadSuccess.pairVol}
                amountOfBoxes={uploadSuccess.boxVol}
                totalValue={uploadSuccess.totalVal}
                loading={loadingSend || loadingDeletion}
                onDelete={() => setModalDeleteOrders(true)}
                onSubmit={handleOrderSend}
              />
            </S.InsertionWrapper>
          </Card>
        )}
        <Modal
          open={modalDeleteOrders}
          setOpen={setModalDeleteOrders}
          title="Excluir"
          showAction={false}
          description="Esta ação excluirá o pedido em aberto e não poderá ser desfeita."
        >
          <S.DeleteOrdersActions>
            <SlimButton
              variant="ghost"
              content="Voltar"
              onClick={() => setModalDeleteOrders(false)}
            />
            <SlimButton
              variant="danger"
              content="Excluir solicitação"
              onClick={handleOrderDeletion}
              loading={loadingDeletion}
            />
          </S.DeleteOrdersActions>
        </Modal>
        {loadingSend && <LoadingModal loadingText="Solicitando materiais..." />}
        <ModalConfirmGeneration
          open={modalConfirmGeneration}
          setOpen={setModalConfirmGeneration}
          onConfirm={() => {
            dispatch(clearGeneratedData())
            setModalConfirmGeneration(false)
          }}
          onReturn={() => {
            dispatch(clearGeneratedData())
            navigate(`${ROUTES.manage.base}`)
          }}
        />
      </S.Wrapper>
    </Page>
  )
}

export default Franchises
